import React, { useState, useRef } from 'react';
import { Box, Container, Snackbar, Alert } from '@mui/material';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import CalendlyBooking from '../components/CalendlyBooking';

const fieldSx = {
  display: 'block',
  width: '100%',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid #2A4560',
  color: '#F0EEE8',
  fontFamily: "'Source Serif 4', Georgia, serif",
  fontStyle: 'italic',
  fontWeight: 300,
  fontSize: '15px',
  padding: '12px 0',
  outline: 'none',
  transition: 'border-color 0.2s',
  '&::placeholder': { color: '#7A9AB0' },
  '&:focus': { borderBottomColor: '#FF914D' },
};

const labelSx = {
  fontSize: '9px', letterSpacing: '3px', textTransform: 'uppercase',
  color: '#FF914D', fontFamily: "'Barlow', sans-serif", display: 'block', mb: 0.5,
};

const errorSx = {
  fontSize: '10px', color: '#FF6B6B',
  fontFamily: "'Barlow', sans-serif", mt: 0.5,
};

const ContactSection = () => {
  const [formValues, setFormValues] = useState({ name: '', email: '', phone: '', projectType: '', message: '' });
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const formRef = useRef();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formValues.name) errors.name = 'Name is required';
    if (!formValues.email) errors.email = 'Email is required';
    else if (!emailRegex.test(formValues.email)) errors.email = 'Invalid email format';
    if (!formValues.message) errors.message = 'Message is required';
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setLoading(true);
    emailjs.sendForm('service_poj6v76', 'template_4l8prdc', formRef.current, 'g0fD3rwDM_UElwin6')
      .then(() => {
        setFormSubmitted(true);
        setLoading(false);
        setFormValues({ name: '', email: '', phone: '', projectType: '', message: '' });
      }, () => {
        setLoading(false);
        setSnackbar({ open: true, message: 'Failed to send message. Please try again later.', severity: 'error' });
      });
  };

  const closeSnackbar = () => setSnackbar(s => ({ ...s, open: false }));

  return (
    <Box id="contact" component="section" sx={{ background: '#12202E', borderBottom: '1px solid #1E3040' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 8, md: 10 },
        }}>
          {/* Left: heading + info + Calendly */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true, margin: '-80px' }}
          >
            <Box sx={{
              fontSize: '10px', letterSpacing: '4px', textTransform: 'uppercase',
              color: '#FF914D', fontFamily: "'Barlow', sans-serif", mb: 2,
              display: 'flex', alignItems: 'center', gap: 1.5,
              '&::before': { content: '""', display: 'block', width: '24px', height: '1px', background: '#FF914D' },
            }}>
              Get In Touch
            </Box>
            <Box component="h2" sx={{
              fontFamily: "'Anton', sans-serif",
              fontSize: { xs: '48px', md: '60px' },
              textTransform: 'uppercase', lineHeight: 0.92,
              color: '#F0EEE8', m: 0, mb: 2.5,
            }}>
              Let's Build<br />Something
            </Box>
            <Box component="p" sx={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontStyle: 'italic', fontWeight: 300,
              fontSize: '16px', color: '#7A9AB0', lineHeight: 1.7, m: 0, mb: 4,
            }}>
              Have a project in mind? I'd love to hear about it. Fill out the form or reach out directly.
            </Box>

            {[
              {
                label: 'Email',
                value: 'ericrak.dev@gmail.com',
                onClick: () => {
                  navigator.clipboard.writeText('ericrak.dev@gmail.com');
                  setSnackbar({ open: true, message: 'Email copied to clipboard!', severity: 'success' });
                },
              },
              { label: 'Location', value: 'Toronto, Ontario' },
              { label: 'Status', value: 'Available for new projects' },
            ].map(item => (
              <Box
                key={item.label}
                onClick={item.onClick}
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 2,
                  py: 2.5, borderBottom: '1px solid #1E3040',
                  cursor: item.onClick ? 'pointer' : 'default',
                  '&:hover .cv': item.onClick ? { color: '#F0EEE8' } : {},
                }}
              >
                <Box sx={{
                  fontSize: '9px', letterSpacing: '3px', textTransform: 'uppercase',
                  color: '#FF914D', fontFamily: "'Barlow', sans-serif",
                  minWidth: '68px', pt: 0.25,
                }}>
                  {item.label}
                </Box>
                <Box className="cv" sx={{
                  fontFamily: "'Source Serif 4', Georgia, serif",
                  fontStyle: 'italic', fontWeight: 300,
                  fontSize: '15px', color: '#7A9AB0', transition: 'color 0.2s',
                }}>
                  {item.value}
                </Box>
              </Box>
            ))}

            <Box sx={{ mt: 4, p: 3, background: '#162333', border: '1px solid #1E3040' }}>
              <Box component="h3" sx={{
                fontFamily: "'Anton', sans-serif", fontSize: '20px',
                textTransform: 'uppercase', color: '#F0EEE8', m: 0, mb: 1.5,
              }}>
                Quick Consultation
              </Box>
              <Box component="p" sx={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontStyle: 'italic', fontWeight: 300,
                fontSize: '14px', color: '#7A9AB0', lineHeight: 1.7, m: 0, mb: 2.5,
              }}>
                Schedule a free 15-minute call to discuss your small business needs.
              </Box>
              <CalendlyBooking buttonProps={{
                sx: {
                  background: 'transparent',
                  border: '1px solid #FF914D',
                  color: '#FF914D',
                  borderRadius: 0,
                  fontSize: '10px',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  fontFamily: "'Barlow', sans-serif",
                  px: 3, py: 1.25,
                  '&:hover': { background: '#FF914D', color: '#0D1821' },
                },
              }} />
            </Box>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            viewport={{ once: true, margin: '-80px' }}
          >
            {formSubmitted ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', height: '100%', gap: 3 }}>
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 20 }}>
                  <Box sx={{ width: 64, height: 64, background: '#1E3040', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #FF914D' }}>
                    <Box component="span" sx={{ color: '#FF914D', fontSize: '28px' }}>✓</Box>
                  </Box>
                </motion.div>
                <Box component="h3" sx={{ fontFamily: "'Anton', sans-serif", fontSize: '32px', textTransform: 'uppercase', color: '#F0EEE8', m: 0 }}>
                  Message Sent
                </Box>
                <Box component="p" sx={{ fontFamily: "'Source Serif 4', Georgia, serif", fontStyle: 'italic', fontWeight: 300, fontSize: '16px', color: '#7A9AB0', lineHeight: 1.7, m: 0 }}>
                  Thank you for reaching out. I'll get back to you as soon as possible.
                </Box>
                <Box component="button" onClick={() => setFormSubmitted(false)} sx={{ background: 'transparent', border: '1px solid #2A4560', color: '#7A9AB0', px: 3, py: 1.25, fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: "'Barlow', sans-serif", cursor: 'pointer', transition: 'all 0.2s', '&:hover': { borderColor: '#7A9AB0', color: '#F0EEE8' } }}>
                  Send Another
                </Box>
              </Box>
            ) : (
              <Box component="form" ref={formRef} onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                  <Box>
                    <Box component="label" sx={labelSx}>Name *</Box>
                    <Box component="input" name="name" value={formValues.name} onChange={handleChange} placeholder="Your name" sx={fieldSx} />
                    {formErrors.name && <Box sx={errorSx}>{formErrors.name}</Box>}
                  </Box>
                  <Box>
                    <Box component="label" sx={labelSx}>Email *</Box>
                    <Box component="input" type="email" name="email" value={formValues.email} onChange={handleChange} placeholder="your@email.com" sx={fieldSx} />
                    {formErrors.email && <Box sx={errorSx}>{formErrors.email}</Box>}
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                  <Box>
                    <Box component="label" sx={labelSx}>Phone</Box>
                    <Box component="input" name="phone" value={formValues.phone} onChange={handleChange} placeholder="Optional" sx={fieldSx} />
                  </Box>
                  <Box>
                    <Box component="label" sx={labelSx}>Business Type</Box>
                    <Box component="select" name="projectType" value={formValues.projectType} onChange={handleChange} sx={{ ...fieldSx, cursor: 'pointer', '& option': { background: '#162333' } }}>
                      <option value="">Select type</option>
                      <option value="retail">Retail Shop</option>
                      <option value="service">Small Business Owner</option>
                      <option value="arts">Artist/Creative</option>
                      <option value="wellness">Health &amp; Wellness</option>
                      <option value="food">Food &amp; Beverage</option>
                      <option value="education">Education &amp; Workshops</option>
                      <option value="other">Other</option>
                    </Box>
                  </Box>
                </Box>
                <Box>
                  <Box component="label" sx={labelSx}>Message *</Box>
                  <Box component="textarea" name="message" rows={5} value={formValues.message} onChange={handleChange} placeholder="Tell me about your project..." sx={{ ...fieldSx, resize: 'vertical' }} />
                  {formErrors.message && <Box sx={errorSx}>{formErrors.message}</Box>}
                </Box>
                <Box
                  component="button"
                  type="submit"
                  disabled={loading}
                  sx={{
                    alignSelf: 'flex-start',
                    background: '#FF914D', color: '#0D1821',
                    border: 'none', px: 5, py: 1.75,
                    fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
                    fontFamily: "'Barlow', sans-serif", fontWeight: 400,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </Box>
              </Box>
            )}
          </motion.div>
        </Box>
      </Container>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={closeSnackbar}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 0 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactSection;
